/**
 * Typed tracked upload API facade over the generated contract client.
 */

import { tenantUploadApi } from '../api/generated/tenantUpload.api.js';
import {
  TrackedFileUploadBodySchema,
  TrackedMultipleFileUploadFormSchema,
  TrackedSingleFileUploadFormSchema,
  type TrackedMultipleFileUploadResponse,
  type TrackedSingleFileUploadResponse,
} from '@cnapse/shared/schemas.js';

class TenantUploadService {
  async deleteFile(filename: string, folder?: string, subFolder?: string): Promise<void> {
    await tenantUploadApi.deleteUploadedFile(
      { filename },
      {
        ...(folder ? { folder } : {}),
        ...(subFolder ? { subFolder } : {}),
      },
    );
  }

  async uploadTrackedFiles(
    files: File[],
    entityType: string,
    entityId?: number,
    fileCategory?: string,
    tags?: string[],
    expiresInDays?: number,
  ): Promise<TrackedMultipleFileUploadResponse['data']> {
    const bodyData = TrackedFileUploadBodySchema.parse({
      entityType,
      entityId,
      fileCategory,
      tags,
      expiresInDays,
    });

    const formBody = TrackedMultipleFileUploadFormSchema.parse({
      ...bodyData,
      files,
      clientOriginalNames: files.map((file) => file.name),
    });

    const data = (await tenantUploadApi.uploadFiles(formBody)) as TrackedMultipleFileUploadResponse['data'];
    if (!data) {
      throw new Error('Upload failed');
    }
    return data;
  }

  async uploadTrackedFile(
    file: File,
    entityType: string,
    entityId?: number,
    fileCategory?: string,
    tags?: string[],
    expiresInDays?: number,
  ): Promise<TrackedSingleFileUploadResponse['data']> {
    const bodyData = TrackedFileUploadBodySchema.parse({
      entityType,
      entityId,
      fileCategory,
      tags,
      expiresInDays,
    });

    const formBody = TrackedSingleFileUploadFormSchema.parse({
      ...bodyData,
      file,
    });

    const data = (await tenantUploadApi.uploadSingle(formBody)) as TrackedSingleFileUploadResponse['data'];
    if (!data) {
      throw new Error('Upload failed');
    }
    return data;
  }

  async linkFilesToEntity(
    objectKeys: string[],
    entityType: string,
    entityId: number,
    fileCategory?: string,
  ): Promise<number> {
    const response = (await tenantUploadApi.linkFiles({
      objectKeys,
      entityType,
      entityId,
      ...(fileCategory ? { fileCategory } : {}),
    })) as { success: boolean; data: { linkedCount: number }; message?: string };

    if (!response.success) {
      throw new Error(response.message || 'Failed to link files');
    }

    return response.data.linkedCount;
  }

  async deleteOrphanedFiles(objectKeys: string[]): Promise<number> {
    const response = (await tenantUploadApi.deleteOrphanedFiles({ objectKeys })) as {
      success: boolean;
      data: { deletedCount: number };
      message?: string;
    };

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete orphaned files');
    }

    return response.data.deletedCount;
  }
}

export const tenantUploadService = new TenantUploadService();
